import React, { useState, useRef, useEffect } from 'react';
import { ModalProps } from '@features/Modal';
import './BlackoutPeriodModal.scss';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    IconButton,
    InputAdornment,
    OutlinedInput,
    TextField,
} from '@mui/material';
import { BiSearch } from 'react-icons/bi';
import { DateSelect } from '@shared/UI';
import { formats } from '@shared/utils';
import moment from 'moment-timezone';
import { useNotify } from '@features/Notify';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { BinRed, EditPencil } from '@pages/LoginWizard/icons';
import { getSecuritiesAll } from '@pages/Preferences/api';
import { ReactComponent as ClockIcon } from '../../../../shared/assets/svgs/ClockIcon.svg';

interface BlackoutPeriodModalProps extends ModalProps {
    list: any;
    setList: React.Dispatch<React.SetStateAction<any>>;
}

export const BlackoutPeriodModal: React.FC<BlackoutPeriodModalProps> = (props) => {
    const { list, setList, onClose } = props;
    const notify = useNotify();

    const [selectedSecurities, setSelectedSecurities] = useState<string[]>([]);
    const [isSecurityOpen, setIsSecurityOpen] = useState(false);
    const [searchSecurityText, setSearchSecurityText] = useState('');
    const securitySearchInputRef = useRef<HTMLInputElement>(null);

    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [listNew, setListNew] = useState<any[]>(list);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [editIndex, setEditIndex] = useState<number | null>(null);

    const [securities, setSecurities] = useState<any[]>([]);
    const [isLoadingSecurities, setIsLoadingSecurities] = useState(false);

    const ALL_OPTION = { symbol: 'ALL' };
    const displaySecurities = [ALL_OPTION, ...securities];

    const toggleSecurityDropdown = () => {
        setIsSecurityOpen((prev) => !prev);
    };

    useEffect(() => {
        const fetchSecurities = async () => {
            try {
                setIsLoadingSecurities(true);
                const result = await getSecuritiesAll(searchSecurityText);
                setSecurities(result);
            } catch (err) {
                notify.error('Failed to fetch securities');
            } finally {
                setIsLoadingSecurities(false);
            }
        };

        fetchSecurities();
    }, [searchSecurityText]);

    const handleSecurityToggle = (symbol: string) => {
        if (symbol === ALL_OPTION.symbol) {
            const isAllSelected = selectedSecurities.includes('ALL');
            setSelectedSecurities(isAllSelected ? [] : ['ALL']);
        } else {
            let updated: string[];

            if (selectedSecurities.includes('ALL')) {
                updated = [symbol];
            } else if (selectedSecurities.includes(symbol)) {
                updated = selectedSecurities.filter((s) => s !== symbol);
            } else {
                updated = [...selectedSecurities, symbol];
            }

            // If all symbols are selected manually, convert to 'ALL'
            const allSymbols = securities.map((s) => s.symbol);
            if (updated.length === allSymbols.length) {
                updated = ['ALL'];
            }

            setSelectedSecurities(updated);
        }
    };

    const handleAdd = () => {
        if (!startDate || !endDate || selectedSecurities.length === 0) {
            setErrorMessage('Please select From Date, To Date, and at least one security.');
            return;
        }

        if (moment(endDate).isBefore(moment(startDate))) {
            setErrorMessage('To Date should be after From Date.');
            return;
        }

        const entry = {
            symbol: selectedSecurities.includes('ALL') ? ['ALL'] : [...selectedSecurities],
            startDate,
            endDate,
        };

        if (editIndex !== null) {
            const updated = [...listNew];
            updated[editIndex] = entry;
            setListNew(updated);
            setEditIndex(null);
        } else {
            setListNew((prevList) => [...prevList, entry]);
        }

        setSelectedSecurities([]);
        setStartDate(null);
        setEndDate(null);
        setSearchSecurityText('');
        setErrorMessage('');
    };

    const handleEdit = (index: number) => {
        const entry = listNew[index];
        const symbolsArray =
            typeof entry.symbol === 'string' ? entry.symbol.split(',').map((s: any) => s.trim()) : entry.symbol;

        setSelectedSecurities(symbolsArray);
        setStartDate(entry.startDate);
        setEndDate(entry.endDate);
        setEditIndex(index);
    };

    const handleDelete = (index: number) => {
        const updated = [...listNew];
        updated.splice(index, 1);
        setListNew(updated);
    };

    const handleUpdate = () => {
        setList(listNew);
        if (onClose) onClose();
    };

    const getDropdownDisplayText = () => {
        if (selectedSecurities.includes('ALL')) return 'ALL Securities Selected';
        if (selectedSecurities.length > 0) return `${selectedSecurities.length} Securities Selected`;
        return 'Select Securities';
    };

    const getSymbolDisplayText = (symbols: string[] | string): string => {
        const list = Array.isArray(symbols) ? symbols : [symbols];
        return list.includes('ALL') ? 'ALL' : list.join(', ');
    };

    return (
        <div className='widget-blackout-table-modal'>
            {/* Header unchanged */}
            <div className='widget-blackout-table-modal__header'>
                <ClockIcon />
                <div className='widget-blackout-table-modal__title'>Add Blackout Period</div>
            </div>

            <div className='widget-blackout-table-modal__content'>
                <Box
                    display='flex'
                    flexDirection='column'
                    gap={2}
                    width='100%'
                >
                    {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}

                    <Box
                        display='flex'
                        gap={2}
                    >
                        <DateSelect
                            currentValue={startDate}
                            placeholder='From Date'
                            dropdownPlacement='bottom-left'
                            format={formats.dateWithYear}
                            onDateSelect={setStartDate}
                        />
                        <DateSelect
                            currentValue={endDate}
                            placeholder='To Date'
                            dropdownPlacement='bottom-left'
                            format={formats.dateWithYear}
                            onDateSelect={setEndDate}
                        />
                        <FormControl className='form-control-full-width'>
                            <div className='dropdown-container'>
                                <OutlinedInput
                                    value={getDropdownDisplayText()}
                                    disabled
                                    className='outlined-input-style'
                                    endAdornment={
                                        <InputAdornment position='end'>
                                            <IconButton
                                                edge='end'
                                                onClick={toggleSecurityDropdown}
                                                className='icon-button-style-caret'
                                            >
                                                {isSecurityOpen ? <FaCaretUp /> : <FaCaretDown />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                {isSecurityOpen && (
                                    <div className='dropdown-menu'>
                                        <Box className='padding-vertical-3 padding-horizontal-10'>
                                            <div className='state-select-container'>
                                                <div>Select Securities</div>
                                                <Button
                                                    variant='text'
                                                    color='primary'
                                                    onClick={() => setSelectedSecurities([])}
                                                    size='small'
                                                    className='small-button'
                                                >
                                                    Clear all
                                                </Button>
                                            </div>
                                            <TextField
                                                placeholder='Search Securities'
                                                variant='outlined'
                                                size='small'
                                                fullWidth
                                                value={searchSecurityText}
                                                onChange={(e) => setSearchSecurityText(e.target.value)}
                                                inputRef={securitySearchInputRef}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position='start'>
                                                            <BiSearch />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Box>
                                        <div className='filter-content'>
                                            {isLoadingSecurities ? (
                                                <div className='filter-option'>Loading...</div>
                                            ) : (
                                                displaySecurities.map((security) => {
                                                    const symbol = security.symbol;
                                                    const isChecked = selectedSecurities.includes('ALL')
                                                        ? symbol === 'ALL'
                                                        : selectedSecurities.includes(symbol);

                                                    return (
                                                        <div
                                                            key={symbol}
                                                            className='filter-option'
                                                        >
                                                            <Checkbox
                                                                onClick={() => handleSecurityToggle(symbol)}
                                                                checked={isChecked}
                                                            />
                                                            <span>{symbol === 'ALL' ? 'ALL' : symbol}</span>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </FormControl>
                        <Button
                            variant='contained'
                            onClick={handleAdd}
                            className='add-blackout-percentage-button'
                        >
                            {editIndex !== null ? 'Update' : 'Add'}
                        </Button>
                    </Box>

                    <Box mt={2}>
                        {listNew.length > 0 && (
                            <>
                                <div className='allocations-list-header'>Added Blackout Period</div>
                                <ul className='list-row-allocations'>
                                    {listNew.map((item, index) => (
                                        <li
                                            key={index}
                                            className='list-row-entry-alloctions'
                                        >
                                            <div className='symbol-with-commas'>
                                                {getSymbolDisplayText(item.symbol)}
                                            </div>
                                            <div className='margin-left-auto'>
                                                {moment(item.startDate).format('MMM D, YYYY')} -{' '}
                                                {moment(item.endDate).format('MMM D, YYYY')}
                                            </div>
                                            <div style={{ marginLeft: '10px' }}>
                                                <IconButton
                                                    size='small'
                                                    onClick={() => handleEdit(index)}
                                                >
                                                    <EditPencil />
                                                </IconButton>
                                                <IconButton
                                                    size='small'
                                                    onClick={() => handleDelete(index)}
                                                >
                                                    <BinRed />
                                                </IconButton>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                        <Button
                            variant='contained'
                            onClick={handleUpdate}
                            className='security-update-button'
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </div>
        </div>
    );
};
